import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import { has, get } from 'Utils/lodashUtils';
import { getSummaryRowTitle } from 'Utils/reportDataUtils';
import DueDate from 'Components/DueDate/DueDate';
import SuccessRate from 'Components/SuccessRate';
import InformationBubble from 'Components/information-bubble/InformationBubble';
import GDCurrencyCodeCell from 'Components/GDTable/GDCurrencyCodeCell';
import { customFormatclass, customClassSummary, customClassCurrency } from '../GDCustomFormat/GDCustomFormat';
import CardType from '../../CardType/CardType';
import './GDTableSummaryRow.scss';
import Badge from '../../Badge/Badge';
import counterpart from 'counterpart';
import SelectInput from '../../forms/select-input/SelectInput';
import { VITE_isFirefox } from 'Constants/appEnvConstants';

const GDTableSummaryRow = ({
  currencyCode,
  extraClass,
  title,
  summaryColumns = [],
  summaryData = [],
  id,
  badge,
  isCaseLanding,
  actionButtonText,
  rowActionClick,
  actions,
  extraWidth,
  rowStyle,
  dateFormat,
  isReadOnly,
  probabilityVerbiage,
  isMerchantUser,
}) => {
  const wrapperClass = classnames('gd-table__summary-row', {
    [extraClass]: extraClass,
  });

  function getCardAbbreviation(cardType) {
    switch (cardType) {
      case 'amex':
      case 'ax':
        return 'AX';
      case 'cb':
        return 'CBRT';
      case 'discover':
      case 'di':
        return 'DI';
      case 'diners':
      case 'dc':
        return 'DC';
      case 'jcb':
        return 'JCB';
      case 'laser':
      case 'lase':
        return 'LASE';
      case 'mastercard':
        case 'mc':
        return 'MC';
      case 'mes':
        return 'MES';
      case 'switchcard':
      case 'swi':
        return 'SWI';
      case 'visa':
      case 'vi':
        return 'VI';
      case 'rupay':
      case 'rp':
        return 'RP';
      case 'unknown':
      case 'un':
        return 'UN';
      default:
        return cardType;
    }
  }

  const divStyle = {
    display: 'flex',
    alignItems: 'center',
  };

  const setMerchantDueDate = (date, disputeStageId, acquirerName, cardBrandCode) => {
    const currentDate = new Date(date);
    if(disputeStageId === 28){
      return moment(currentDate).format("MMM. D, YYYY");
    }
    else if(acquirerName === 'HPYPD' && disputeStageId === 2 && !['CO', 'EBT', 'IN', 'QU', 'SO'].includes(cardBrandCode)){
      return moment(currentDate).subtract(10, "days").format("MMM. D, YYYY");
    }
    else{
      return moment(currentDate).subtract(5, "days").format("MMM. D, YYYY");
    }
  }

  const displayedTitle = getSummaryRowTitle(currencyCode, title, summaryData);

  const summaryRow = summaryData.length ? (
    <div className={wrapperClass} style={rowStyle}>

      {currencyCode && <div style={divStyle} className="gd-table__summary-row-title" id={`${id}_${displayedTitle}_lbl`}>
        <h1 style={{ paddingRight: 50 }}>{displayedTitle}</h1>
        <Badge style={{ width: '150px' }} badgeType={badge}>
          {badge}
        </Badge>

        <br />
      </div>}
      <ul className={isCaseLanding ? 'gd-table__summary-row-list__caselanding' : 'gd-table__summary-row-list'}>
        {summaryColumns.map((column, index) => {
          let valueClass;
          if (index === 0) {
            if (column.displayType === 'CURRENCY' && Number.parseFloat(get(summaryData[0], column.columnKey)) < 0) {
              valueClass = 'gd-table__summary-row-list-item-amount primary_bracket';
            } else if (Number.parseFloat(get(summaryData[0], column.columnKey)) < 0) {
              valueClass = 'gd-table__summary-row-list-item-amount primary_bracket';
            } else {
              valueClass = 'gd-table__summary-row-list-item-amount primary';
            }
          } else
            if ((column.displayType === 'CURRENCY' || column.displayType === 'NEGATIVE_NUMBER' && customClassSummary) && Number.parseFloat(get(summaryData[0], column.columnKey)) < 0) {
              valueClass = customFormatclass(customClassSummary, column.displayType);
            } else if (Number.parseFloat(get(summaryData[0], column.columnKey)) < 0) {
              valueClass = customFormatclass(customClassSummary, column.displayType);
            }


          const handlePrecision = (amount, precision) => {
            if (amount !== undefined && amount !== null) {
              const arr = `${amount}`.split('.');
              return arr.length > 1
                ? `${arr[0] <= 0 ? arr[0] * -1 : arr[0]}.${arr[1].length === 1 ? `${arr[1]}0` : arr[1].substr(0, precision)}`
                : `${amount <= 0 ? amount * -1 : amount}.00`;
            }

            return '--';
          };

          const getCurrencyCell = (amount, currency, columnKey1, displayType, currIndex) => {
            if (currency !== undefined) {
              const normalizedValue = amount === 0 ? 0 : amount || '--';
              const normalizedUnits = currency || '';
              let valueClass;
              if (Number.isNaN(Number.parseFloat(amount))) {
                return <span key={`${currIndex}-${columnKey1}`}>{normalizedValue}</span>;
              }

              if (!Number.isNaN(Number.parseFloat(normalizedValue)) && Number.parseFloat(normalizedValue) < 0) { valueClass = customFormatclass(customClassCurrency, displayType); }
              return (
                <GDCurrencyCodeCell
                  key={`${currIndex}-${columnKey1}`}
                  value={normalizedValue}
                  units={normalizedUnits}
                  customClass={valueClass}
                  headerClass={isCaseLanding ? 'headerClass' : ''}
                />
              );
            } else {
              let valueClass;
              const normalizedValue = amount !== '' ? amount : '--';
              if (!Number.isNaN(Number.parseFloat(normalizedValue)) && Number.parseFloat(normalizedValue) < 0) { valueClass = customFormatclass(customClassCurrency, item.displayType); }
              return (
                <GDCurrencyCodeCell
                  key={`${currIndex}-${columnKey1}`}
                  value={normalizedValue}
                  customClass={valueClass}
                  headerClass={isCaseLanding ? 'headerClass' : ''}
                />
              );
            }
          };

          let value;

          if (column.displayType === 'cardType') {
            value = (
              <div style={{ marginBottom: '4px', width: '100%' }}>

                {has(summaryData[0], column.columnKey) && (
                  <CardType
                    abbreviation={getCardAbbreviation(get(summaryData[0], column.columnKey).toLowerCase())}
                    type={get(summaryData[0], column.columnKey).toLowerCase()}
                    key={`${index}-${get(summaryData[0], column.columnKey)}`}
                  />
                )}

              </div>


            );
          } else if (column.displayType === 'CURRENCY') {
            value = handlePrecision(get(summaryData[0], column.columnKey), 2);
          } else if (column.displayType === 'NEGATIVE_NUMBER') {
            value = handlePrecision(get(summaryData[0], column.columnKey), 2);
          } else if (column.displayType === 'DATE' && has(summaryData[0], column.columnKey) &&  get(summaryData[0], column.columnKey) !== null) {
            const dateFields = get(summaryData[0], column.columnKey).split('-');
            const date = dateFields ? `${dateFields[1]}/${dateFields[2]}/${dateFields[0]}` : '';
            if(date) 
              value = isCaseLanding ? moment(date).format("MMM. D, YYYY") : date; 
            else 
              value = '--';
          } else if (column.displayType === 'CURRENCY_UNIT') {
            value = (<span className="currency-cell">{getCurrencyCell(get(summaryData[0], column.columnKey.value1), get(summaryData[0], column.columnKey.value2), column.columnKey.value1, column.displayType, index)}</span>);
          } else if (column.displayType === 'DUEDATE') {
            value = !!get(summaryData[0],column.columnKey) ? moment(get(summaryData[0],column.columnKey)).format(dateFormat) : '--';
          }
          else {
            value = !!get(summaryData[0],column.columnKey) ? get(summaryData[0],column.columnKey) : '--';
          }
          return (

            column.displayType != 'DAYSLEFT' && column.displayType != 'PROBABILITY' ?
              <li
                className={isCaseLanding ? 'gd-table__summary-row-list-item__caselanding' : 'gd-table__summary-row-list-item'}
                key={`${index}-${column.columnKey}`}
              >
                <div className="gd-table__summary-row-list-item-wrapper">
                  <span id={`${id}_${column.name}_lbl`} className="gd-table__summary-row-list-item-label">
                    {column.name}
                  </span>
                  <span id={`${id}_${value}_value`} className={valueClass || `gd-table__summary-row-list-item-amount ${isMerchantUser && column.displayType == 'RC' ? 'click' : ''}`}
                    onClick={isMerchantUser && column.displayType == 'RC' ? () => {rowActionClick(0,'RCA')} : () => {}}
                  >{column.name !== 'Merchant Due Date' ? value : setMerchantDueDate(value, summaryData[0].caseStageDetail.disputeStageId, summaryData[0].caseStageDetail.acquirerName, summaryData[0].caseStageDetail.cardBrandCode)}
                    {(column.displayType == 'RC' || column.displayType == 'DATE') && <InformationBubble
                      id={column.descriptionKey}
                      info
                      place={'left'}
                      tooltipContent={column.descriptionKey === 'merchantDueDate' ? 'Merchant response must be received by this date. Letter respond by dates cannot exceed this.' : (column.descriptionKey === 'cardbrandDueDate' ? 'Response to issuer must be processed by this date. Cases passed this timeframe will be expired.' : (summaryData[0][column.descriptionKey] !== null || column.descriptionKey !== 'merchantDueDate' || column.descriptionKey !== 'cardbrandDueDate' ?  get(summaryData[0],column.descriptionKey) : ''))}
                    />
                    }
                  </span>
                </div>
              </li> : column.displayType === 'DAYSLEFT' ? <DueDate key={`${index}-${column.columnKey}`} daysLeft={get(summaryData[0],column.columnKey)} />

                : column.displayType === 'PROBABILITY' && get(summaryData[0],column.columnKey) !== undefined && !(get(summaryData[0],column.columnKey) < 0) &&
                <li
                  className={isCaseLanding ? 'gd-table__summary-row-list-item__caselanding probability' : 'gd-table__summary-row-list-item'}
                  key={`${index}-${column.columnKey}`}
                >
                  <div className="gd-table__summary-row-list-item-wrapper">
                    <span id={`${id}_${column.name}_lbl`} className="gd-table__summary-row-list-item-label">
                      {column.name}
                    </span>
                    <span style={{ float: 'right',   display: 'flex', }}>
                      <SuccessRate
                        percentage={get(summaryData[0],column.columnKey)}
                      />
                      {(get(summaryData[0],column.columnKey) < 100) &&
                        <InformationBubble
                          id={column.descriptionKey}
                          probability
                          place={'left'}
                          tooltipContent={probabilityVerbiage}
                        />
                      }
                    </span>
                  </div>
                </li>
          );
        })}
      </ul>
      {isReadOnly ? <span className="read-only-mode">{isCaseLanding || actions &&
        <SelectInput
          dataList={actions}
          selectClass={VITE_isFirefox ? { marginLeft: '-77px' } : { marginLeft: '-73px' }}
          extraClass="action-popup-wrapper"
          handleSelection={rowActionClick}
          promptText={counterpart('globalTranslate.takeAction.takeAction')}
          selectedItem={null}
          wrapperClass="select-menu__form reports"
          textColor="#007dc3"
          isReadOnly={isReadOnly}
        />}</span>
        :
        <span>
          {isCaseLanding || actions &&
            <SelectInput
              dataList={actions}
              selectClass={VITE_isFirefox ? { marginLeft: '-77px' } : { marginLeft: '-73px' }}
              extraClass="action-popup-wrapper"
              handleSelection={rowActionClick}
              promptText={actionButtonText}
              selectedItem={null}
              wrapperClass="select-menu__form reports"
              textColor="#007dc3"
            />}
        </span>}

    </div>
  ) : null;

  return summaryRow;
};

GDTableSummaryRow.propTypes = {
  currencyCode: PropTypes.string,
  extraClass: PropTypes.string,
  summaryColumns: PropTypes.array.isRequired,
  summaryData: PropTypes.array.isRequired,
  title: PropTypes.string,
  badge: PropTypes.string,
  isCaseLanding: PropTypes.bool,
  actionButtonText: PropTypes.string,
  rowActionClick: PropTypes.func,
  actions: PropTypes.array,
  extraWidth: PropTypes.bool,
  dateFormat: PropTypes.string.isRequired,
  probabilityVerbiage: PropTypes.string,
  isMerchantUser: PropTypes.bool,
};

GDTableSummaryRow.defaultProps = {
  currencyCode: '',
  extraClass: '',
  title: '',
  dateFormat: '',
  isCaseLanding: false,
  rowStyle: {},
  probabilityVerbiage: '',
  isMerchantUser: false,
};

export default GDTableSummaryRow;
